import { useIntl, WithSkeleton } from '@capsicum/common';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { type FontVariants, SkeletonItem, Text } from '../../atoms';

type Props = {
	children?: string;
	maxLines: number;
	variant: FontVariants;
};

const ExpandableTextSkeleton = ({ mainComponentProps }: { mainComponentProps: Props }) => (
	<S.SkeletonItemStyled $maxLines={mainComponentProps.maxLines} width="100%" />
);

export const ExpandableTextComponent = ({ children, maxLines = 3, variant }: Props) => {
	const { $t } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (textRef.current) {
			const lineHeight = parseInt(getComputedStyle(textRef.current).lineHeight || '0');
			const maxHeight = lineHeight * maxLines;

			if (textRef.current.scrollHeight > maxHeight) {
				setIsTruncated(true);
			}
		}
	}, [maxLines, children]);

	const toggleExpansion = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div>
			<S.TextContainer $isExpanded={isExpanded} $maxLines={maxLines} ref={textRef} variant={variant}>
				{children}
			</S.TextContainer>
			{isTruncated && (
				<S.Toggle test-id="expandable-text-toggle" variant={variant} onClick={toggleExpansion}>
					{isExpanded ? $t('global.showLess') : $t('global.readMore')}
				</S.Toggle>
			)}
		</div>
	);
};

export const ExpandableText = WithSkeleton(ExpandableTextComponent, ExpandableTextSkeleton);

namespace S {
	export const TextContainer = styled(Text)<{ $isExpanded: boolean; $maxLines: number }>`
		max-height: ${({ $isExpanded, $maxLines }) => ($isExpanded ? 'none' : `${$maxLines * 1.5}rem`)};
		overflow: hidden;
		text-overflow: ellipsis;
		display: ${({ $isExpanded }) => ($isExpanded ? 'block' : '-webkit-box')};
		-webkit-line-clamp: ${({ $isExpanded, $maxLines }) => ($isExpanded ? 'none' : $maxLines)};
		-webkit-box-orient: ${({ $isExpanded }) => ($isExpanded ? 'unset' : 'vertical')};
	`;

	export const Toggle = styled(Text)`
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		cursor: pointer;
	`;

	export const SkeletonItemStyled = styled(SkeletonItem)<{ $maxLines: number }>`
		height: ${({ $maxLines }) => `${$maxLines * 1.5}rem`};
	`;
}
